import NextLink from "next/link";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { createContext, CSSProperties, forwardRef, Fragment, memo, useContext } from "react";
import CN from "classnames";

import { DreamIDType, RenderCuidLink } from "../RenderCuidLink";
import { capitalCase } from "@portal/app/_common/lib/changeCase";
import { sentenceCase } from "@portal/app/_common/lib/changeCase";
import { renderDollarAmount, renderPhoneNumber } from "@portal/app/_common/lib/formatRenderers";
import { getDateFormatted, getDateTimeFormatted } from "@portal/app/_common/lib/dateTimeUtils";
import { TimeZoneContext } from "../TimeZoneContext";
import { DateTime } from "luxon";
import { styled, type Theme, useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons/faArrowRight";
import { faCircleRight } from "@fortawesome/pro-solid-svg-icons/faCircleRight";
import { adminDensity } from "../theme/adminDensity";
import type { AdminTheme } from "../theme/adminTheme";

export type DreamTableCellDensity = "standard" | "compact";

/**
 * AdminDataTable publishes density to cell renderers without changing their
 * long-standing render signature. Legacy DreamTable deliberately leaves this
 * unset so school/customer spacing remains unchanged.
 */
export const DreamTableCellDensityContext = createContext<DreamTableCellDensity | null>(null);

/**
 * Standard TableCell, with a few bells and whistles:
 *
 * ## Supports clickable and linkable behavior
 *
 * Adding an `onClick` handler adds a hover effect. Adding an `href` will wrap
 * the cell in a `next/link` Link. Both will apply click targets to the entire
 * cell, not just the text inside, and will add an arrow icon to the end of
 * `children`.
 *
 * ## Supports Tooltip
 *
 * Passing `tooltip` will display that content in a Tooltip.
 */
export const DreamTableCell = memo(
  forwardRef(
    (
      {
        children,
        onClick,
        href,
        linkAriaLabel,
        openInNewTab,
        tooltip,
        tooltipDescribeChild,
        noWrap = true,
        arrow = true,
        maxWidth,
        style,
        ...props
      }: React.ComponentProps<typeof TableCell> & {
        // URI to be passed to `next/link` Link
        href?: string;

        // If true, open `href` link in new tab.
        openInNewTab?: boolean;

        // Accessible name for links whose visible content is only an icon.
        linkAriaLabel?: string;

        // Title to be passed to Tooltip
        tooltip?: string;

        /*
        Passed through to MUI's `describeChild`. Off by default, which is what
        MUI does: the tooltip becomes the cell's `aria-label`. That REPLACES the
        accessible name, so a cell whose tooltip names only part of what it
        renders announces only that part. Turn this on for those cells and the
        tooltip describes the cell instead of renaming it.
        */
        tooltipDescribeChild?: boolean;
        /*
        If True (default), `white-space: nowrap` is passed such that text
        is not allowed to break onto the next line. Since tables often try
        to fit as much as possible, this avoids excessive line breaks in
        favor of a wider table. Use by default but be aware of circumstances
        where this is not preferred.
        */
        noWrap?: boolean;
        /*
        If True (default), an arrow icon is added if the cell has clickable
        behavior. (Passing True without a click handler will not add an arrow.)
         */
        arrow?: boolean;
        /*
        When set, constrains the cell width and clips overflow with an
        ellipsis. If `tooltip` is not explicitly passed and `children` is a
        plain string, the full string is auto-shown in a Tooltip on hover.
        For ReactNode children (e.g. nested elements), pass `tooltip`
        explicitly with the value you want the user to see.
        */
        maxWidth?: number | string;
      },
      ref
    ) => {
      const contextDensity = useContext(DreamTableCellDensityContext);
      const theme = useTheme<Theme & { admin?: AdminTheme["admin"] }>();
      // Standalone tables do not publish the density context. Inside the admin
      // shell they still receive the admin TableCell theme, so keep full-cell
      // links on that same standard geometry. Shared school/customer tables
      // retain their long-standing padding when the admin marker is absent.
      const density = contextDensity ?? (theme.admin ? "standard" : null);
      const isClickable = !!href || !!onClick;
      const isTruncated = maxWidth !== undefined;

      // Auto-derive tooltip from string children when truncating. ReactNode
      // children must pass tooltip explicitly — we don't try to extract text
      // from a tree.
      const resolvedTooltip =
        tooltip ?? (isTruncated && typeof children === "string" ? children : undefined);

      const content = (
        <>
          {children}
          {isClickable && arrow && (
            <BaseCellIcon>
              <FontAwesomeIcon icon={faArrowRight} />
            </BaseCellIcon>
          )}
        </>
      );

      const mergedStyle = maxWidth !== undefined ? { maxWidth, ...(style ?? {}) } : style;

      const component = (
        <BaseCell
          onClick={onClick}
          ref={ref}
          style={mergedStyle}
          className={CN(props.className, {
            clickable: isClickable,
            "no-wrap": noWrap,
            truncate: isTruncated,
          })}
          {...props}
        >
          {href ? (
            <BaseCellLink
              aria-label={linkAriaLabel}
              data-cell-density={density ?? undefined}
              href={href}
              target={openInNewTab ? "_blank" : undefined}
              $density={density}
            >
              {content}
            </BaseCellLink>
          ) : (
            <>{content}</>
          )}
        </BaseCell>
      );

      return resolvedTooltip ? (
        <Tooltip title={resolvedTooltip} describeChild={tooltipDescribeChild} arrow>
          {component}
        </Tooltip>
      ) : (
        component
      );
    }
  )
);
DreamTableCell.displayName = "DreamTableCell";

/**
 * Cell that displays a truncated ID, with an optional link.
 */
export const DreamTableLinkedIdCell = memo(({ id, link }: { id: string; link?: string }) => (
  <DreamTableCell
    href={link}
    component="th"
    tooltip={id}
    scope="row"
    sx={{ font: "code", width: 0 }}
  >
    &hellip;
    <Typography variant="mono">{id.slice(id.length - 8)}</Typography>
  </DreamTableCell>
));
DreamTableLinkedIdCell.displayName = "DreamTableLinkedIdCell";

/**
 * Cell that displays its contents in a monospace font.
 */
export const DreamTableCodeCell = memo(
  ({ children, ...propsToCell }: React.ComponentProps<typeof DreamTableCell>) => (
    <DreamTableCell {...propsToCell}>
      {/* `component="span"` keeps the inner element inline so the parent
          cell's `text-overflow: ellipsis` applies when `maxWidth` is set —
          a block-level wrapper would break the ellipsis chain. */}
      <Typography variant="mono" component="span">
        {children}
      </Typography>
    </DreamTableCell>
  )
);
DreamTableCodeCell.displayName = "DreamTableCodeCell";

/**
 * Cell that displays a timestamp in relative format ("3 days ago"), with a
 * Tooltip on hover to display the full date.
 */
export const DreamTableRelativeTimestampCell = memo(({ timestamp }: { timestamp: string }) => {
  const fullString = DateTime.fromISO(timestamp).toLocaleString(DateTime.DATETIME_FULL);
  return (
    <DreamTableCell tooltip={fullString}>
      {sentenceCase(DateTime.fromISO(timestamp).toRelativeCalendar() || fullString)}
    </DreamTableCell>
  );
});
DreamTableRelativeTimestampCell.displayName = "DreamTableRelativeTimestampCell";

export const DreamTableTimestampCell = memo(({ timestamp }: { timestamp: string }) => (
  <DreamTableCell>
    {DateTime.fromISO(timestamp).toLocaleString(DateTime.DATETIME_SHORT)}
  </DreamTableCell>
));
DreamTableTimestampCell.displayName = "DreamTableTimestampCell";

export const DreamTableDateCell = memo(({ date }: { date: string }) => (
  <DreamTableCell>
    {DateTime.fromISO(date).toUTC().toLocaleString(DateTime.DATE_SHORT)}
  </DreamTableCell>
));
DreamTableDateCell.displayName = "DreamTableDateCell";

export const DreamTableDollarAmountInCentsCell = memo(
  ({ amount }: { amount: number | null | undefined }) => (
    <DreamTableCell>{renderDollarAmount(amount, true, false)}</DreamTableCell>
  )
);
DreamTableDollarAmountInCentsCell.displayName = "DreamTableDollarAmountInCentsCell";

/*
 * Cell that displays a hyperlinked icon.
 */
export const DreamTableViewRowButton = memo(({ href, label }: { href: string; label: string }) => (
  <DreamTableCell
    arrow={false}
    href={href}
    linkAriaLabel={label}
    sx={{ textAlign: "center", color: "primary.main" }}
  >
    <FontAwesomeIcon icon={faCircleRight} />
  </DreamTableCell>
));

/**
 * @deprecated Use DreamTableLinkedIdCell instead
 */
export const IdCell = memo(
  ({
    children,
    type,
    link,
  }: {
    children: string | undefined | null;
    type: DreamIDType;
    link: string;
  }) => (
    <TableCell component="th" scope="row" style={{ width: 100 }}>
      <RenderCuidLink code={children} type={type} link={link} />
    </TableCell>
  )
);
IdCell.displayName = "IdCell";

export const TextCell = memo(
  ({
    children,
    isPrimary = false,
    style,
  }: React.PropsWithChildren<{
    isPrimary?: boolean;
    style?: CSSProperties;
  }>) => {
    const Wrapper = isPrimary ? "strong" : Fragment;

    return (
      <TableCell style={style}>
        <Wrapper>{children ?? ""}</Wrapper>
      </TableCell>
    );
  }
);

TextCell.displayName = "TextCell";

export const TitleCell = memo(({ children }: { children: string | null | undefined }) => (
  <TextCell>{capitalCase(children ?? "")}</TextCell>
));
TitleCell.displayName = "TitleCell";

export const DeprecatedDateCell = memo(
  ({
    children,
    format = "MMMM d, y",
  }: {
    children: string | null | undefined;
    format?: string;
  }) => {
    const formattedDate = DateTime.fromISO(children ?? "").toUTC();
    return <TextCell>{formattedDate.isValid ? formattedDate.toFormat(format) : ""}</TextCell>;
  }
);
DeprecatedDateCell.displayName = "DeprecatedDateCell";

export const DateCell = memo(
  ({
    children = "",
    format = "MMMM d, y",
  }: {
    children: string | null | undefined;
    format?: string;
  }) => <TextCell>{getDateFormatted(children, format)}</TextCell>
);
DateCell.displayName = "DateCell";

export const DateTimeCell = memo(
  ({
    children = "",
    format = "f",
    timeZone,
  }: {
    children: string | null | undefined;
    format?: string;
    timeZone?: string;
  }) => <TextCell>{getDateTimeFormatted(children, format, timeZone)}</TextCell>
);
DateTimeCell.displayName = "DateTimeCell";

export const ContextDateTimeCell = memo(
  ({ children = "", format = "f" }: { children: string | null | undefined; format?: string }) => {
    const { timeZone } = useContext(TimeZoneContext);
    if (!timeZone) {
      throw "ContextDateTimeCell must only be used inside of a TimeZoneContext provider";
    }
    return (
      <DateTimeCell format={format} timeZone={timeZone}>
        {children}
      </DateTimeCell>
    );
  }
);
ContextDateTimeCell.displayName = "ContextDateTimeCell";

export const PhoneCell = memo(({ children }: { children: string | null | undefined }) => (
  <TextCell>{renderPhoneNumber(children ?? "")}</TextCell>
));
PhoneCell.displayName = "PhoneCell";

export const CurrencyCell = memo(
  ({
    children,
    shouldRenderZero = false,
  }: {
    children: number | null | undefined;
    shouldRenderZero?: boolean;
  }) => <DreamTableCodeCell>{renderDollarAmount(children, shouldRenderZero)}</DreamTableCodeCell>
);
CurrencyCell.displayName = "CurrencyCell";

export const ActionCell = memo(
  ({ children }: { children: React.ReactNode | React.ReactNode[] }) => (
    <TableCell>
      <Box display="inline-flex" flexWrap="wrap">
        {!!(children as []).map
          ? (children as React.ReactNode[]).map((child, i) => (
              <Box p={1} key={i}>
                {child}
              </Box>
            ))
          : children}
      </Box>
    </TableCell>
  )
);
ActionCell.displayName = "ActionCell";

export const CustomCell = memo(
  ({ children, style }: React.PropsWithChildren<{ style?: CSSProperties }>) => (
    <TableCell style={style}>{children}</TableCell>
  )
);
CustomCell.displayName = "CustomCell";

const BaseCell = styled(TableCell)`
  &.clickable {
    cursor: pointer;
    transition: 100ms linear all;
    padding: 0;

    a {
      display: block;
      width: 100%;
    }

    &:hover {
      background: ${({ theme }) => theme.palette.action.hover};
    }
  }

  &.no-wrap {
    white-space: nowrap;
  }

  &.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const BaseCellLink = styled(NextLink, {
  shouldForwardProp: (prop) => prop !== "$density",
})<{ $density: DreamTableCellDensity | null }>`
  box-sizing: border-box;
  min-height: ${({ $density }) =>
    $density === "standard"
      ? `${adminDensity.table.standardRow}px`
      : $density === "compact"
        ? `${adminDensity.table.compactRow}px`
        : "auto"};
  padding: ${({ $density }) => {
    if ($density === "standard") {
      return `${adminDensity.table.standardCellPaddingBlock}px ${adminDensity.table.cellPaddingInline}px`;
    }
    if ($density === "compact") {
      return `${adminDensity.table.compactCellPaddingBlock}px ${adminDensity.table.cellPaddingInline}px`;
    }
    return "1rem";
  }};
  text-decoration: none;
  color: ${({ theme }) => theme.palette.dream.grayDark};

  ${({ theme }) => theme.breakpoints.down("sm")} {
    ${({ $density }) =>
      $density
        ? `
          min-height: ${adminDensity.control.touch}px;
          padding: 10px ${adminDensity.table.cellPaddingInline}px;
        `
        : ""}
  }
`;

const BaseCellIcon = styled(Box)`
  display: inline-block;
  font-size: 0.75em;
  margin-left: 0.75em;
  color: ${({ theme }) => theme.palette.primary.light};
`;
